import { Box, Divider, Flex, Text } from '@chakra-ui/react'
import { ContractionIntensity, ContractionRestingTone } from '@hb/shared'
import { format } from 'date-fns'
import type { FC, MouseEvent } from 'react'
import {
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceArea,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts'

import { ChartLegend } from './ChartLegend'
import type { LaborChartXAxisProps } from './DilationStationGraph'

// ── Helpers ────────────────────────────────────────────────────────────────

const intensityColor = (i: ContractionIntensity) => {
  switch (i) {
    case ContractionIntensity.MILD:
      return '#A0AEC0'
    case ContractionIntensity.MODERATE:
      return '#F6AD55'
    case ContractionIntensity.STRONG:
      return '#ED8936'
    case ContractionIntensity.VERY_STRONG:
      return '#E53E3E'
  }
}

const fmtIntensity = (i: ContractionIntensity) => i.charAt(0).toUpperCase() + i.slice(1)

const restingToneColor = (r: ContractionRestingTone) => {
  switch (r) {
    case ContractionRestingTone.SOFT:
      return '#48BB78'
    case ContractionRestingTone.FIRM:
      return '#F6AD55'
    case ContractionRestingTone.HYPERTONIC:
      return '#E53E3E'
  }
}

const fmtRestingTone = (r: ContractionRestingTone) => r.charAt(0).toUpperCase() + r.slice(1)

const frequencyColor = (freq: number) => (freq >= 6 ? '#E53E3E' : freq >= 5 ? '#ED8936' : '#718096')

// ── Shared Contraction Display ─────────────────────────────────────────────

export interface ContractionExamDisplayProps {
  frequency: number
  duration: number
  intensity: ContractionIntensity
  restingTone: ContractionRestingTone
  collectedVia: 'manual' | 'instrument'
  notes?: string
}

/** Full contraction summary — used in both the tooltip and the entry click popover. */
export const ContractionExamDisplay = ({
  frequency,
  duration,
  intensity,
  restingTone,
  collectedVia,
  notes,
}: ContractionExamDisplayProps) => {
  const freqCol = frequencyColor(frequency)
  const intCol = intensityColor(intensity)
  const toneCol = restingToneColor(restingTone)

  return (
    <Flex direction="column" gap={0.5}>
      {/* Primary: frequency + duration */}
      <Flex justify="space-between" align="center">
        <Text fontSize="xs" color="gray.400">
          Frequency
        </Text>
        <Flex align="baseline" gap={1}>
          <Text fontSize="sm" fontWeight={700} color={freqCol}>
            {frequency}
          </Text>
          <Text fontSize="xs" color={freqCol}>
            / 10 min
          </Text>
        </Flex>
      </Flex>
      <Flex justify="space-between" align="center">
        <Text fontSize="xs" color="gray.400">
          Duration
        </Text>
        <Flex align="baseline" gap={1}>
          <Text fontSize="sm" fontWeight={600} color="gray.600" _dark={{ color: 'gray.300' }}>
            {duration}
          </Text>
          <Text fontSize="xs" color="gray.400">
            s
          </Text>
        </Flex>
      </Flex>

      {/* Quality */}
      <Divider my={1} />
      <Flex justify="space-between" align="center">
        <Text fontSize="xs" color="gray.400">
          Intensity
        </Text>
        <Text fontSize="xs" fontWeight={600} color={intCol}>
          {fmtIntensity(intensity)}
        </Text>
      </Flex>
      <Flex justify="space-between" align="center">
        <Text fontSize="xs" color="gray.400">
          Resting Tone
        </Text>
        <Text fontSize="xs" fontWeight={600} color={toneCol}>
          {fmtRestingTone(restingTone)}
        </Text>
      </Flex>
      <Flex justify="space-between" align="center">
        <Text fontSize="xs" color="gray.400">
          Collected Via
        </Text>
        <Text fontSize="xs" color="gray.600" _dark={{ color: 'gray.300' }}>
          {collectedVia === 'manual' ? 'Manual' : 'Instrument'}
        </Text>
      </Flex>

      {/* Notes */}
      {notes ? (
        <>
          <Divider my={1} />
          <Text
            fontSize="xs"
            fontWeight={600}
            letterSpacing="wider"
            color="gray.400"
            _dark={{ color: 'gray.500' }}
            mb={0.5}>
            NOTE
          </Text>
          <Text fontSize="xs" color="gray.700" _dark={{ color: 'gray.200' }} whiteSpace="pre-wrap">
            {notes}
          </Text>
        </>
      ) : null}
    </Flex>
  )
}

// ── Legend items ───────────────────────────────────────────────────────────

const CONTRACTIONS_LEGEND_ITEMS = [
  {
    kind: 'line' as const,
    label: 'Contractions (per 10 min)',
    color: '#718096',
  },
  {
    kind: 'band' as const,
    label: 'Duration (bar width, color = intensity)',
    fill: '#A0AEC0',
  },
]

// ── Data + Tooltip ─────────────────────────────────────────────────────────

export interface ContractionPoint {
  time: number
  frequency: number
  duration?: number
  intensity?: ContractionIntensity
  restingTone?: ContractionRestingTone
  collectedVia?: 'manual' | 'instrument'
  notes?: string
  entryId?: string
}

const safeFormatTime = (ms: number) => {
  try {
    return format(new Date(ms), 'HH:mm')
  } catch {
    return ''
  }
}

const ContractionTooltip = ({
  active,
  payload,
}: {
  active?: boolean
  payload?: Array<{ payload?: ContractionPoint; value?: number }>
}) => {
  if (!active || !payload?.length) return null
  const pt = payload[0]?.payload
  if (!pt) return null

  const { time, frequency, duration, intensity, restingTone, collectedVia, notes } = pt

  const hasFullData =
    duration != null && intensity != null && restingTone != null && collectedVia != null

  return (
    <Box
      bg="white"
      _dark={{ bg: 'gray.800', borderColor: 'whiteAlpha.300' }}
      border="1px solid"
      borderColor="gray.200"
      borderRadius={8}
      px={3}
      py={2.5}
      shadow="md"
      fontFamily="hero-new"
      fontSize="sm"
      minW="200px"
      maxW="280px">
      {time != null ? (
        <Text fontWeight={600} color="gray.500" _dark={{ color: 'gray.400' }} mb={1.5}>
          {safeFormatTime(time)}
        </Text>
      ) : null}
      {hasFullData ? (
        <>
          <Divider mb={1.5} />
          <ContractionExamDisplay
            frequency={frequency}
            duration={duration}
            intensity={intensity}
            restingTone={restingTone}
            collectedVia={collectedVia}
            notes={notes}
          />
        </>
      ) : (
        <>
          <Divider mb={1.5} />
          <Text color="#718096">Frequency: {frequency} / 10 min</Text>
        </>
      )}
    </Box>
  )
}

// ── Graph ──────────────────────────────────────────────────────────────────

export const ContractionsGraph: FC<{
  contractionPoints: ContractionPoint[]
  xAxisProps: LaborChartXAxisProps
  gridColor: string
  tickStyle: { fontFamily: string; fontSize: number; fill: string }
  onChartClick: (data: any, event: MouseEvent) => void
}> = ({ contractionPoints, xAxisProps, gridColor, tickStyle, onChartClick }) => (
  <>
    <Box w="100%" h="180px" pl={1} pr={4} cursor="pointer">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart
          data={contractionPoints}
          margin={{ top: 8, right: 52, left: 8, bottom: 8 }}
          syncId="labor"
          onClick={onChartClick}>
          <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
          <XAxis {...xAxisProps} />
          <YAxis
            domain={[0, 7]}
            ticks={[0, 1, 2, 3, 4, 5, 6, 7]}
            tick={tickStyle}
            label={{
              value: '/ 10 min',
              angle: -90,
              position: 'middle',
              style: { fontFamily: 'hero-new', fontSize: 14, fill: '#718096' },
            }}
          />
          <RechartsTooltip content={<ContractionTooltip />} />
          {contractionPoints.map((pt, idx) =>
            pt.duration != null ? (
              <ReferenceArea
                key={pt.entryId ?? idx}
                x1={pt.time}
                x2={pt.time + pt.duration * 1000}
                y1={pt.frequency - 0.25}
                y2={pt.frequency + 0.25}
                fill={pt.intensity != null ? intensityColor(pt.intensity) : '#718096'}
                fillOpacity={0.65}
                strokeWidth={0}
              />
            ) : null,
          )}
          <Line
            dataKey="frequency"
            stroke="#718096"
            strokeWidth={2}
            dot={{ r: 4, fill: '#718096', stroke: '#fff', strokeWidth: 1.5 }}
            activeDot={{ r: 7 }}
            isAnimationActive={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </Box>
    <ChartLegend items={CONTRACTIONS_LEGEND_ITEMS} />
  </>
)
